import type { IDomain } from "@bprotsyk/aso-core";
import { useCallback, useEffect, useState } from "react";
import { Message, toaster } from "rsuite";
import ContainerTable from "./table/table";
import EditingRecordsModal from "../../modals/domains/host-settings";
import StaticModal from "../../../ui/modals/domains/add-static-files";
import { PanelService } from "network/panel/network";
import { createLogger } from "../../../utils/logger";
import { isPdlApplicationDomain } from "./pdl-domain-list-policy";
import "./_domains.scss";

const logger = createLogger("domains");
const HEADER_HEIGHT = 56;
const SUB_HEADER_HEIGHT = 64;
const TOTAL_HEADER_HEIGHT = HEADER_HEIGHT + SUB_HEADER_HEIGHT;
const placement = "topEnd";

export default function DomainsPage() {
  const [editingModalOpen, setEditingModalOpen] = useState(false);
  const [staticModalOpen, setStaticModalOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<IDomain | null>(null);
  const [data, setData] = useState<IDomain[]>([]);
  const [load, setLoad] = useState(false);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - TOTAL_HEADER_HEIGHT);
  const [namecheapBalance, setNamecheapBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceUnavailable, setBalanceUnavailable] = useState(false);

  const getTableData = useCallback(async () => {
    try {
      const domains = await PanelService.getDomains();
      setData(Array.isArray(domains) ? domains : []);
    } catch (error) {
      logger.error("Failed to load PDL application domains", error);
      setData([]);
    }
  }, []);

  const getNamecheapBalance = useCallback(async () => {
    setBalanceLoading(true);
    setBalanceUnavailable(false);
    try {
      const value = Number(await PanelService.getDomainBalance());
      if (!Number.isFinite(value) || value < 0) {
        throw new Error("Invalid Namecheap balance response");
      }
      setNamecheapBalance(value);
    } catch (error) {
      logger.error("Failed to load Namecheap balance", error);
      setNamecheapBalance(null);
      setBalanceUnavailable(true);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    void Promise.all([getTableData(), getNamecheapBalance()]);
  }, [getNamecheapBalance, getTableData]);

  useEffect(() => {
    const handleResize = () => {
      setTableHeight(window.innerHeight - TOTAL_HEADER_HEIGHT);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleEditingDomain = (domain: IDomain) => {
    setEditingDomain({
      ...domain,
      txtRecords: Array.isArray(domain.txtRecords) ? domain.txtRecords : [],
    } as IDomain);
    setEditingModalOpen(true);
  };

  const uploadStaticToServer = async (file: File) => {
    if (!editingDomain?.name) return;

    const name = editingDomain.name;
    setLoad(true);
    try {
      await PanelService.uploadStatic({ file, name });
      const fresh = await PanelService.getDomains();
      setData(Array.isArray(fresh) ? fresh : []);
      const updatedDomain = fresh.find((item: IDomain) => item.name === name);
      if (updatedDomain) setEditingDomain(updatedDomain);
    } finally {
      setLoad(false);
    }
  };

  const domainUpdate = (updateData: Partial<IDomain>) => {
    setData((previousDomains) => previousDomains.map((domain) => (
      domain.id === updateData.id ? { ...domain, ...updateData } as IDomain : domain
    )));
  };

  const handleRenewDomain = async (domain: IDomain) => {
    try {
      const result = await PanelService.renewDomain({ domainName: domain.name, years: 1 });
      if (!result.success) throw new Error(result.message);

      toaster.push(
        <Message showIcon type="success" closable>
          Домен {domain.name} успішно поновлено до {new Date(result.data.expiredDate).toLocaleDateString("uk-UA")}
        </Message>,
        { placement, duration: 5000 }
      );
      await Promise.all([getTableData(), getNamecheapBalance()]);
    } catch (error: any) {
      logger.error("Domain renewal error", error);
      toaster.push(
        <Message showIcon type="error" closable>
          Помилка при поновленні домену {domain.name}: {error?.message || "Невідома помилка"}
        </Message>,
        { placement, duration: 5000 }
      );
    }
  };

  const applicationDomains = data.filter(isPdlApplicationDomain);
  const balanceText = balanceLoading
    ? "Завантаження..."
    : balanceUnavailable || namecheapBalance === null
      ? "Недоступний"
      : `$${namecheapBalance.toFixed(2)}`;

  return (
    <div className="domains">
      <div className="domains-header">
        <div className="domains-header-content">
          <div className="domains-actions">
            <div
              className={`namecheap-balance-chip${balanceUnavailable ? " is-unavailable" : ""}`}
              aria-live="polite"
              title={balanceUnavailable ? "Не вдалося отримати баланс Namecheap" : "Поточний баланс Namecheap"}
            >
              <span>Namecheap</span>
              <strong>{balanceText}</strong>
            </div>
          </div>
        </div>
      </div>

      <ContainerTable
        onHandleEditingDomain={handleEditingDomain}
        searchQuery=""
        tableData={applicationDomains}
        tableHeight={Math.max(tableHeight, 320)}
        onRenewDomain={handleRenewDomain}
      />

      {editingModalOpen && editingDomain && (
        <EditingRecordsModal
          editingDomainData={editingDomain}
          onEditingModal={() => setEditingModalOpen(false)}
          onStaticModal={() => setStaticModalOpen((open) => !open)}
          load={load}
          onDomainDataUpdate={domainUpdate}
        />
      )}

      {staticModalOpen && (
        <StaticModal
          onSetEditStatic={() => setStaticModalOpen(false)}
          onUploads={uploadStaticToServer}
        />
      )}
    </div>
  );
}
